/**
 * LibraryGenerator - Handles library-specific code generation
 * Only generates types/protocols for the current library package
 */

import fs from "fs-extra";
import path from "path";
import pc from "picocolors";
import type { ComponentConfig } from "./main-index";
import type { ParsedModule } from "./parser";
import type { PlatformRegistry } from "./platforms";

export interface LibraryGenerationResult {
  generatedFiles: string[];
  warnings: string[];
}

export class LibraryGenerator {
  private config: ComponentConfig;
  private platformRegistry?: PlatformRegistry;

  constructor(config: ComponentConfig, platformRegistry?: PlatformRegistry) {
    this.config = config;
    this.platformRegistry = platformRegistry;
  }

  /**
   * Main library generation method - Generates only types/protocols for current library
   */
  async generateLibraryCode(modules: ParsedModule[]): Promise<string[]> {
    const allGeneratedFiles: string[] = [];

    if (!this.platformRegistry) {
      this.log(
        "⚠️  No platform registry available, skipping library generation"
      );
      return allGeneratedFiles;
    }

    this.log(`📦 Generating library code for ${modules.length} modules...`);

    // Generate iOS library code (types and protocols)
    if (this.shouldGenerateIos()) {
      try {
        const iosFiles = await this.generateIosLibraryCode(modules);
        allGeneratedFiles.push(...iosFiles);
        this.log(`✅ Generated ${iosFiles.length} iOS library files`);
      } catch (error) {
        this.logError(
          `iOS library generation failed: ${
            error instanceof Error ? error.message : String(error)
          }`
        );
      }
    }

    // Generate Android library code (types and interfaces)
    if (this.shouldGenerateAndroid()) {
      try {
        const androidFiles = await this.generateAndroidLibraryCode(modules);
        allGeneratedFiles.push(...androidFiles);
        this.log(`✅ Generated ${androidFiles.length} Android library files`);
      } catch (error) {
        this.logError(
          `Android library generation failed: ${
            error instanceof Error ? error.message : String(error)
          }`
        );
      }
    }

    return allGeneratedFiles;
  }

  /**
   * Generates iOS library code (Swift types and protocols)
   */
  private async generateIosLibraryCode(
    modules: ParsedModule[]
  ): Promise<string[]> {
    const generatedFiles: string[] = [];
    // Use configured type output path
    const iosDir = path.join(this.config.projectRoot, "ios/typegen");

    // Ensure directory exists
    await fs.ensureDir(iosDir);

    for (const module of modules) {
      try {
        // Generate Swift types file
        const typesFile = await this.generateSwiftTypes(module, iosDir);
        generatedFiles.push(typesFile);

        // Generate Swift protocol file (includes event helpers)
        const protocolFile = await this.generateSwiftProtocol(module, iosDir);
        generatedFiles.push(protocolFile);

        this.log(`📱 Generated iOS library files for ${module.moduleName}`);
      } catch (error) {
        this.logError(
          `Failed to generate iOS library files for ${module.moduleName}: ${
            error instanceof Error ? error.message : String(error)
          }`
        );
      }
    }

    return generatedFiles;
  }

  /**
   * Generates Android library code (Kotlin types and interfaces)
   */
  private async generateAndroidLibraryCode(
    modules: ParsedModule[]
  ): Promise<string[]> {
    const generatedFiles: string[] = [];
    // Use configured type output path as base
    const baseAndroidDir = path.join(
      this.config.projectRoot,
      "android",
      "src",
      "main",
      "kotlin"
    );

    for (const module of modules) {
      try {
        // Get the Android package from brickConfig or directly from sourceModule
        const androidPackage =
          module.sourceModule?.brickConfig?.android?.package ||
          module.sourceModule?.androidPackage;

        if (!androidPackage) {
          // For library modules, the brickConfig might not be present in sourceModule
          // Skip Android generation if no package is defined
          this.log(
            `⚠️  Skipping Android generation for ${module.moduleName}: no android.package defined`
          );
          continue;
        }

        // Convert package name to directory path (e.g., com.brickcalculator -> com/brickcalculator)
        const packagePath = androidPackage.replace(/\./g, "/");
        // Add typegen subdirectory after package path for library modules
        const androidDir = path.join(baseAndroidDir, packagePath, "typegen");

        // Ensure android directory exists
        await fs.ensureDir(androidDir);

        // Generate Kotlin types file
        const typesFile = await this.generateKotlinTypes(module, androidDir);
        generatedFiles.push(typesFile);

        // Generate Kotlin interface file
        const interfaceFile = await this.generateKotlinInterface(
          module,
          androidDir
        );
        generatedFiles.push(interfaceFile);

        this.log(
          `🤖 Generated Android library files for ${module.moduleName} in ${androidDir}`
        );
      } catch (error) {
        this.logError(
          `Failed to generate Android library files for ${module.moduleName}: ${
            error instanceof Error ? error.message : String(error)
          }`
        );
      }
    }

    return generatedFiles;
  }

  /**
   * Generates Swift types file for library
   */
  private async generateSwiftTypes(
    module: ParsedModule,
    outputDir: string
  ): Promise<string> {
    const filename = `${module.moduleName}Types.swift`;
    const outputPath = path.join(outputDir, filename);

    // Use platform registry to generate Swift types
    if (this.platformRegistry) {
      const iosPlatform = this.platformRegistry.getIOSPlatform();
      if (iosPlatform) {
        const content = await iosPlatform.generateLibraryTypes(module);

        await fs.writeFile(outputPath, content);
        this.logGenerated(outputPath);
      }
    }

    return outputPath;
  }

  /**
   * Generates Swift protocol file for library
   */
  private async generateSwiftProtocol(
    module: ParsedModule,
    outputDir: string
  ): Promise<string> {
    const filename = `${module.moduleName}Spec.swift`;
    const outputPath = path.join(outputDir, filename);

    // Use platform registry to generate Swift protocol
    if (this.platformRegistry) {
      const iosPlatform = this.platformRegistry.getIOSPlatform();
      if (iosPlatform) {
        let content = await iosPlatform.generateLibraryProtocol(module);

        // Append event helpers if module has events
        if (module.events && module.events.length > 0) {
          const eventHelpers = this.generateEventHelpersExtension(module);
          content += `\n${eventHelpers}`;
        }

        await fs.writeFile(outputPath, content);
        this.logGenerated(outputPath);
      }
    }

    return outputPath;
  }

  /**
   * Generates event helper extension for module
   */
  private generateEventHelpersExtension(module: ParsedModule): string {
    const helpers = (module.events || [])
      .map((e) => {
        const cap = e.name.charAt(0).toUpperCase() + e.name.slice(1);
        const eventKey = `on${cap}`;
        return `    public func emitOn${cap}(payload: [String: Any]) {\n        emit("${eventKey}", payload: payload)\n    }`;
      })
      .join("\n\n");

    return `// Typed Event Emit Helpers for ${module.moduleName}
extension ${module.moduleName}Spec where Self: BrickModuleBase {
${helpers}
}
`;
  }

  /**
   * Generates Kotlin types file for library
   */
  private async generateKotlinTypes(
    module: ParsedModule,
    outputDir: string
  ): Promise<string> {
    const filename = `${module.moduleName}Types.kt`;
    const outputPath = path.join(outputDir, filename);

    // Use platform registry to generate Kotlin types
    if (this.platformRegistry) {
      const androidPlatform = this.platformRegistry.getAndroidPlatform();
      if (androidPlatform) {
        const content = await androidPlatform.generateLibraryTypes(module);

        await fs.writeFile(outputPath, content);
        this.logGenerated(outputPath);
      }
    }

    return outputPath;
  }

  /**
   * Generates Kotlin interface file for library
   */
  private async generateKotlinInterface(
    module: ParsedModule,
    outputDir: string
  ): Promise<string> {
    const filename = `${module.moduleName}Spec.kt`;
    const outputPath = path.join(outputDir, filename);

    // Use platform registry to generate Kotlin interface
    if (this.platformRegistry) {
      const androidPlatform = this.platformRegistry.getAndroidPlatform();
      if (androidPlatform) {
        const content = await androidPlatform.generateLibraryInterface(module);

        await fs.writeFile(outputPath, content);
        this.logGenerated(outputPath);
      }
    }

    return outputPath;
  }

  /**
   * Generate types for local modules in app projects
   * This is called for modules found via *.brick.ts scanning in app projects
   */
  async generateLocalModuleTypes(modules: ParsedModule[]): Promise<string[]> {
    const generatedFiles: string[] = [];

    // Filter to only local modules (not from node_modules or library packages)
    // Local modules are those with spec files directly in the project, not from packages
    const localModules = modules.filter((module) => {
      const modulePath = module.sourceModule?.path || "";
      // Check if it's from node_modules (including symlinked workspace packages)
      const isFromNodeModules = modulePath.includes("node_modules");
      // Check if it has a package.json (library package)
      const isLibraryPackage = module.sourceModule?.brickConfig !== undefined;

      // Only include if it's NOT from node_modules AND NOT a library package
      return !isFromNodeModules && !isLibraryPackage;
    });

    if (localModules.length === 0) {
      this.log(`📦 No local modules found (all modules are from packages)`);
      return generatedFiles;
    }

    this.log(
      `📦 Generating types for ${localModules.length} local modules to typegen/...`
    );

    // Use the same methods as library generation but only for local modules
    // Generate iOS types
    try {
      const iosFiles = await this.generateIosLibraryCode(localModules);
      generatedFiles.push(...iosFiles);
      this.log(
        `✅ Generated ${iosFiles.length} iOS type files for local modules`
      );
    } catch (error) {
      this.logError(
        `iOS type generation failed for local modules: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    }

    // Generate Android types
    try {
      const androidFiles = await this.generateAndroidLibraryCode(localModules);
      generatedFiles.push(...androidFiles);
      this.log(
        `✅ Generated ${androidFiles.length} Android type files for local modules`
      );
    } catch (error) {
      this.logError(
        `Android type generation failed for local modules: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    }

    return generatedFiles;
  }

  private shouldGenerateIos(): boolean {
    const selectedPlatforms = this.config.selectedPlatforms;
    return !selectedPlatforms || selectedPlatforms.includes("ios");
  }

  private shouldGenerateAndroid(): boolean {
    const selectedPlatforms = this.config.selectedPlatforms;
    return !selectedPlatforms || selectedPlatforms.includes("android");
  }

  private log(message: string): void {
    console.log(`${pc.bold(pc.green("[LibraryGen]"))} ${message}`);
  }

  private logError(message: string): void {
    console.error(`${pc.bold(pc.green("[LibraryGen]"))} ${pc.red(message)}`);
  }

  private logGenerated(filepath: string): void {
    const absolutePath = path.resolve(filepath);
    console.log(
      `${pc.bold(pc.green("[LibraryGen]"))} Generated artifact: ${absolutePath}`
    );
  }
}
